import { __ } from "@wordpress/i18n";
import { useSettings } from "../../context/SettingsContext";
import { Toggle, SettingRow, SectionCard, NumberInput, InfoBox, ButtonGroup } from "../ui/index";

const MOBILE_PG_POSITIONS = [
    { value: "bottom", label: __("Bottom", "wpbean-product-gallery-slider-for-woocommerce") },
    { value: "left",   label: __("Left", "wpbean-product-gallery-slider-for-woocommerce") },
    { value: "right",  label: __("Right", "wpbean-product-gallery-slider-for-woocommerce") },
];

const MOBILE_THUMB_POSITIONS = [
    { value: "bottom", label: __("Bottom", "wpbean-product-gallery-slider-for-woocommerce") },
    { value: "top",    label: __("Top", "wpbean-product-gallery-slider-for-woocommerce") },
    { value: "left",   label: __("Left", "wpbean-product-gallery-slider-for-woocommerce") },
    { value: "right",  label: __("Right", "wpbean-product-gallery-slider-for-woocommerce") },
];

export default function MobilePanel() {
    const {
        state: { settings },
        actions,
    } = useSettings();
    const s = (key, val) => actions.set(key, val);

    return (
        <div className="space-y-5">
            <SectionCard
                title={__("Mobile Fallback", "wpbean-product-gallery-slider-for-woocommerce")}
                description={__("Below this breakpoint, Anchor Navigation and Sticky Split galleries collapse to a standard carousel. The Thumbnail Gallery applies mobile-specific arrow, pagination, and thumbnail settings instead.", "wpbean-product-gallery-slider-for-woocommerce")}
            >
                <SettingRow
                    label={__("Mobile Breakpoint", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Viewport width below which mobile settings take effect. Anchor and Sticky Split switch to a standard carousel; Thumbnail Gallery applies the mobile navigation and thumbnail options below. Set to 0 to disable.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <NumberInput
                        value={settings.mobileBreakpoint}
                        onChange={(v) => s("mobileBreakpoint", v)}
                        min={0}
                        max={1280}
                        unit="px"
                        width="w-24"
                    />
                </SettingRow>

                {settings.mobileBreakpoint > 0 && (
                    <>
                        <SettingRow
                            label={__("Navigation Arrows", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__("Show previous/next arrow buttons on the mobile carousel. Independent of the desktop arrow setting.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <Toggle
                                value={settings.mobileArrows !== false}
                                onChange={(v) => s("mobileArrows", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label={__("Dot Pagination", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__("Show dot indicators below the mobile carousel for quick navigation.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <Toggle
                                value={settings.mobilePagination !== false}
                                onChange={(v) => s("mobilePagination", v)}
                            />
                        </SettingRow>

                        {settings.mobilePagination !== false && (
                            <SettingRow
                                label={__("Dot Position", "wpbean-product-gallery-slider-for-woocommerce")}
                                description={__("Where the pagination dots appear in the mobile carousel.", "wpbean-product-gallery-slider-for-woocommerce")}
                            >
                                <ButtonGroup
                                    options={MOBILE_PG_POSITIONS}
                                    value={settings.mobilePaginationPosition || "bottom"}
                                    onChange={(v) => s("mobilePaginationPosition", v)}
                                />
                            </SettingRow>
                        )}

                        <SettingRow
                            label={__("Show Thumbnails", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__("Show the thumbnail strip in the mobile carousel fallback. Off by default for a cleaner mobile experience.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <Toggle
                                value={settings.mobileShowThumbs}
                                onChange={(v) => s("mobileShowThumbs", v)}
                            />
                        </SettingRow>

                        {settings.mobileShowThumbs && (
                            <>
                                <SettingRow
                                    label={__("Thumbnail Position", "wpbean-product-gallery-slider-for-woocommerce")}
                                    description={__("Where the thumbnail strip appears on mobile. Independent of the desktop thumbnail position.", "wpbean-product-gallery-slider-for-woocommerce")}
                                >
                                    <ButtonGroup
                                        options={MOBILE_THUMB_POSITIONS}
                                        value={settings.mobileThumbnailPosition || "bottom"}
                                        onChange={(v) => s("mobileThumbnailPosition", v)}
                                    />
                                </SettingRow>

                                <SettingRow
                                    label={__("Thumbnail Width", "wpbean-product-gallery-slider-for-woocommerce")}
                                    description={__("Width of each thumbnail item on mobile. Set to 0 to use the desktop thumbnail width.", "wpbean-product-gallery-slider-for-woocommerce")}
                                >
                                    <NumberInput
                                        value={settings.mobileThumbnailWidth}
                                        onChange={(v) => s("mobileThumbnailWidth", v)}
                                        min={0}
                                        max={300}
                                        unit="px"
                                        width="w-24"
                                    />
                                </SettingRow>

                                <SettingRow
                                    label={__("Thumbnail Height", "wpbean-product-gallery-slider-for-woocommerce")}
                                    description={__("Height of each thumbnail item on mobile. Set to 0 to use the desktop thumbnail height.", "wpbean-product-gallery-slider-for-woocommerce")}
                                >
                                    <NumberInput
                                        value={settings.mobileThumbnailHeight}
                                        onChange={(v) => s("mobileThumbnailHeight", v)}
                                        min={0}
                                        max={300}
                                        unit="px"
                                        width="w-24"
                                    />
                                </SettingRow>
                            </>
                        )}
                    </>
                )}

            </SectionCard>

            <SectionCard
                title={__("Touch Zoom", "wpbean-product-gallery-slider-for-woocommerce")}
                description={__("Pinch-to-zoom behavior on touch devices.", "wpbean-product-gallery-slider-for-woocommerce")}
            >
                <SettingRow
                    label={__("Enable Touch Zoom", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Allow users to pinch to zoom the main image on mobile and tablet devices.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <Toggle
                        value={settings.touchZoom}
                        onChange={(v) => s("touchZoom", v)}
                    />
                </SettingRow>

                {settings.touchZoom && (
                    <>
                        <SettingRow
                            label={__("Minimum Scale", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__("Minimum zoom level (1 = original size).", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <NumberInput
                                value={settings.touchZoomMin}
                                onChange={(v) => s("touchZoomMin", v)}
                                min={1}
                                max={3}
                                step={0.1}
                            />
                        </SettingRow>

                        <SettingRow
                            label={__("Maximum Scale", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__("Maximum zoom level users can reach by pinching.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <NumberInput
                                value={settings.touchZoomMax}
                                onChange={(v) => s("touchZoomMax", v)}
                                min={1.5}
                                max={10}
                                step={0.5}
                            />
                        </SettingRow>
                    </>
                )}
            </SectionCard>

            <SectionCard
                title={__("Swipe Navigation", "wpbean-product-gallery-slider-for-woocommerce")}
                description={__("Swipe gesture settings for navigating the gallery.", "wpbean-product-gallery-slider-for-woocommerce")}
            >
                <SettingRow
                    label={__("Swipe Threshold", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Minimum swipe distance in pixels required to trigger a gallery slide.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <NumberInput
                        value={settings.swipeThreshold}
                        onChange={(v) => s("swipeThreshold", v)}
                        min={10}
                        max={200}
                        unit="px"
                    />
                </SettingRow>
            </SectionCard>
        </div>
    );
}
